/**
 * Interfaces: the program's ICD - what crosses between its parts, and how
 * (issue #52).
 *
 * This screen exists because the generic `EntityList` answered /interfaces with
 * one flat table of messages, port defs and interface defs, differing only in a
 * "Sort" column, and one New button that asked for a keyword and a free-text
 * type. None of the three is made that way:
 *
 *   - an INTERFACE is a type (which files it in the ICD and brings what it must
 *     state), two ends and what flows between them;
 *   - a PORT DEF is what crosses it and which way - or, for a connector, its
 *     pins and what each carries;
 *   - a MESSAGE is its fields and its rate;
 *   - and the TYPES the interfaces are built from are the program's own, on the
 *     library's Software, Electrical and Mechanical disciplines.
 *
 * So each has a tab and a New that asks its own questions, and one pane shows
 * whichever element is selected. /interfaces/{id} still falls through to the
 * generated editor, which the pane links to.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import DownloadIcon from '@mui/icons-material/Download'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tabs from '@mui/material/Tabs'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { ApiError, PAGE_LIMIT, api } from '../api'
import EmptyState from '../components/common/EmptyState'
import HelpTip from '../components/common/HelpTip'
import EntityFilterBar from '../components/entity/EntityFilterBar'
import { matchesQuery, readSearch, writeSearch } from '../components/entity/rowFilter'
import { useRememberedFilters } from '../components/entity/rememberedFilters'
import IcdElementPane from '../components/interfaces/IcdElementPane'
import InterfaceDefWizard from '../components/interfaces/InterfaceDefWizard'
import InterfaceTypesPanel from '../components/interfaces/InterfaceTypesPanel'
import MessageWizard from '../components/interfaces/MessageWizard'
import PortDefWizard from '../components/interfaces/PortDefWizard'
import { tail } from '../components/interfaces/icd-model'
import type { EntityRow, InterfaceTypeRow } from '../schema'
import { useCanWrite, useRefreshAppData } from '../state/AppData'
import { MONOSPACE } from '../theme'

type View = 'interface' | 'port' | 'message' | 'types'

const TABS: { value: View; label: string; help: string }[] = [
  {
    value: 'interface', label: 'Interfaces',
    help: 'Each interface definition: what kind it is, the two ends it joins and what flows ' +
      'between them. Connect two parts’ ports with one from a part’s pane on Parts.',
  },
  {
    value: 'port', label: 'Port definitions',
    help: 'What crosses a port and which way - or, for a connector, its pins. A part’s ' +
      'port is typed by one of these; the mating side is its conjugate (~).',
  },
  {
    value: 'message', label: 'Messages',
    help: 'The payloads that cross a software interface: their fields and how often they are sent.',
  },
  {
    value: 'types', label: 'Types',
    help: 'What kind of interface something is. Build your own on the library’s Software, ' +
      'Electrical and Mechanical disciplines.',
  },
]

export default function Interfaces() {
  const permission = useCanWrite()
  const refreshSchema = useRefreshAppData()
  const navigate = useNavigate()
  const [view, setView] = useState<View>('interface')
  /**
   * The search, in the URL under `search` as on every other list (issue #86).
   *
   * It was component state, so it was gone the moment anything else was opened
   * and the box was empty on the way back. In the URL it is a link, and
   * remembered so that the sidebar and the breadcrumbs put it back. Replace, not
   * push: typing must not fill up the back button.
   */
  const [params, setParams] = useSearchParams()
  const search = readSearch(params)
  const setSearch = (next: string) =>
    setParams((now) => writeSearch(now, next), { replace: true })
  useRememberedFilters()
  const [rows, setRows] = useState<EntityRow[] | null>(null)
  const [types, setTypes] = useState<InterfaceTypeRow[]>([])
  const [error, setError] = useState<ApiError | null>(null)
  const [selected, setSelected] = useState('')
  const [creating, setCreating] = useState<'' | 'interface' | 'port' | 'connector' | 'message'>('')
  const [reloads, setReloads] = useState(0)

  useEffect(() => {
    let live = true
    api.list('interfaces', { limit: PAGE_LIMIT })
      .then((page) => live && setRows(page.items))
      .catch((failure: ApiError) => live && setError(failure))
    api.interfaceTypes()
      .then((found) => live && setTypes(found))
      .catch(() => live && setTypes([]))
    return () => {
      live = false
    }
  }, [reloads])

  const listed = useMemo(
    () => (rows ?? [])
      .filter((row) => (row as unknown as Record<string, unknown>).declared_as === view)
      .filter((row) => matchesQuery(row, search)),
    [rows, view, search],
  )

  // Which disciplines an interface is filed under, from its type's name.
  const disciplinesOf = useCallback((row: EntityRow) => {
    const written = String((row as unknown as Record<string, unknown>).type_name ?? '')
    const names = written.split(',').map((entry) => tail(entry.trim()))
    return [...new Set(types.filter((type) => names.includes(type.name)).flatMap((type) => type.disciplines))]
  }, [types])

  const created = (identifier: string) => {
    setCreating('')
    setSelected(identifier)
    setReloads((count) => count + 1)
  }

  if (error) {
    return (
      <EmptyState
        title="Could not load the interfaces"
        description={error.message}
        actions={[{ label: 'Try again', onClick: () => setReloads((count) => count + 1) }]}
      />
    )
  }

  const tab = TABS.find((entry) => entry.value === view)!
  const newButton = (label: string, what: typeof creating) => (
    <Tooltip title={permission.allowed ? '' : permission.reason} describeChild key={what}>
      <span>
        <Button
          variant={what === 'connector' ? 'outlined' : 'contained'}
          startIcon={<AddIcon />}
          onClick={() => setCreating(what)}
          disabled={!permission.allowed}
        >
          {label}
        </Button>
      </span>
    </Tooltip>
  )

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
          <Typography variant="h5">Interfaces</Typography>
          <HelpTip title={tab.help} label="How to read this tab" />
        </Box>
        {view !== 'types' && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            href={api.entityCsvUrl('interfaces')}
            download="interfaces.csv"
          >
            Export CSV
          </Button>
        )}
        {view === 'interface' && newButton('New interface definition', 'interface')}
        {view === 'port' && [newButton('New connector', 'connector'), newButton('New port definition', 'port')]}
        {view === 'message' && newButton('New message', 'message')}
      </Stack>

      <Tabs
        value={view}
        onChange={(_event, next: View) => {
          setView(next)
          setSelected('')
        }}
      >
        {TABS.map((entry) => <Tab key={entry.value} value={entry.value} label={entry.label} />)}
      </Tabs>

      {view === 'types' ? (
        <Paper sx={{ p: 2 }}>
          <InterfaceTypesPanel
            onChanged={() => {
              setReloads((count) => count + 1)
              // A new type, or a new attribute on one, changes what /api/schema
              // offers - the types an interface may be built from and the fields
              // each brings - and the schema is read once, at start.
              void refreshSchema()
            }}
          />
        </Paper>
      ) : (
        <>
          <EntityFilterBar plural="interfaces" value={search} onChange={setSearch} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: selected ? 7 : 12 }}>
              <Paper sx={{ p: 1.5 }}>
                {!rows ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : listed.length === 0 ? (
                  <EmptyState
                    title={search ? `Nothing matches “${search}”` : `No ${tab.label.toLowerCase()} yet`}
                    description={search ? undefined : tab.help}
                  />
                ) : (
                  <Table size="small" aria-label={tab.label}>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>{view === 'interface' ? 'Type' : 'Built on'}</TableCell>
                        {view === 'interface' && <TableCell>Discipline</TableCell>}
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {listed.map((row) => {
                        const record = row as unknown as Record<string, unknown>
                        return (
                          <TableRow
                            key={row.qualified_name || row.id}
                            hover
                            selected={row.id === selected}
                            onClick={() => setSelected(row.id)}
                            onDoubleClick={() => navigate(`/interfaces/${encodeURIComponent(row.id)}`)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell sx={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}>{row.id}</TableCell>
                            <TableCell>{row.name}</TableCell>
                            <TableCell sx={{ fontFamily: MONOSPACE, fontSize: '0.8rem' }}>
                              {String(record.type_name ?? '').split(',').map((entry) => tail(entry.trim())).join(', ')}
                            </TableCell>
                            {view === 'interface' && (
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  {disciplinesOf(row).map((discipline) => (
                                    <Chip key={discipline} size="small" label={discipline} />
                                  ))}
                                </Box>
                              </TableCell>
                            )}
                            <TableCell sx={{ fontSize: '0.8rem', maxWidth: '40ch' }}>
                              {String(record.statement ?? '')}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                )}
                {view === 'interface' && rows && types.every((type) => type.in_library) && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    This program declares no interface types of its own yet. An interface can be typed by
                    a library discipline directly, or declare types on the Types tab first.
                  </Alert>
                )}
              </Paper>
            </Grid>
            {selected && (
              <Grid size={{ xs: 12, lg: 5 }}>
                <Paper sx={{ p: 2 }}>
                  <IcdElementPane identifier={selected} types={types} />
                </Paper>
              </Grid>
            )}
          </Grid>
        </>
      )}

      {creating === 'interface' && (
        <InterfaceDefWizard onClose={() => setCreating('')} onCreated={created} />
      )}
      {(creating === 'port' || creating === 'connector') && (
        <PortDefWizard
          initialSort={creating === 'connector' ? 'connector' : 'flow'}
          onClose={() => setCreating('')}
          onCreated={created}
        />
      )}
      {creating === 'message' && <MessageWizard onClose={() => setCreating('')} onCreated={created} />}
    </Stack>
  )
}
